import {PasswordInput, Stack, TextInput} from '@mantine/core';
import React, {useState} from 'react';

import {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Modal from 'ui-library/modals/Modal';
import {getUserMessage} from 'util/Net';
import {withLoadingScreen} from 'util/Promises';

import {GatewayType} from './InstrumentGatewayConstants';
import {createGateway} from './InstrumentGatewayDAO';

export interface CreateDatabricksGatewayModalProps {
  onRequestClose: () => void;
  onSuccess?: () => void;
}

const GENERIC_VALIDATION_FAILURE_MESSAGE = 'Validation failed. Please check your input and try again.';

export function CreateDatabricksGatewayModal(props: CreateDatabricksGatewayModalProps): JSX.Element {
  const {onRequestClose, onSuccess} = props;
  const [error, setError] = useState<string | null>(null);

  const [gatewayName, setGatewayName] = useState('');
  const [databricksHost, setDatabricksHost] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [clientId, setClientId] = useState('');
  const [secret, setSecret] = useState('');

  const allRequiredFieldsFilled = Boolean(
    gatewayName.trim() && databricksHost.trim() && warehouseId.trim() && secret.trim()
  );

  function buildGatewayFields(): Record<string, string> {
    const fields: Record<string, string> = {
      connection_mode: 'DIRECT',
      databricks_host: databricksHost.trim(),
      databricks_warehouse_id: warehouseId.trim(),
      databricks_secret: secret.trim(),
    };
    // Without a client ID, the secret is used as a personal access token.
    if (clientId.trim()) {
      fields.databricks_client_id = clientId.trim();
    }
    return fields;
  }

  return (
    <Modal
      buttons={[
        {
          style: ButtonStyle.PRIMARY,
          text: 'Configure gateway',
          disabled: !allRequiredFieldsFilled,
          submit: true,
        },
      ]}
      title="Configure Databricks gateway"
      onRequestClose={onRequestClose}
      closeable={true}
      bannerProps={error != null ? {bannerType: InlineBannerType.ERROR, text: error} : null}
      onRequestSubmit={async () => {
        setError(null);
        try {
          await withLoadingScreen(
            createGateway(gatewayName, GatewayType.DATABRICKS, buildGatewayFields()),
            'Setting up Databricks gateway...'
          );
          onSuccess?.();
          onRequestClose();
        } catch (err: unknown) {
          const userMessage = getUserMessage(err);
          if (userMessage == null) {
            setError(GENERIC_VALIDATION_FAILURE_MESSAGE);
            throw err;
          }
          setError(userMessage);
        }
      }}
    >
      <Stack spacing="md">
        <TextInput
          label="Name"
          required={true}
          value={gatewayName}
          onChange={(e) => {
            setGatewayName(e.currentTarget.value);
          }}
        />
        <TextInput
          label="Workspace URL"
          description="e.g. https://dbc-1234abcd-5678.cloud.databricks.com"
          required={true}
          value={databricksHost}
          onChange={(e) => {
            setDatabricksHost(e.currentTarget.value);
          }}
        />
        <TextInput
          label="SQL warehouse ID"
          required={true}
          value={warehouseId}
          onChange={(e) => {
            setWarehouseId(e.currentTarget.value);
          }}
        />
        <TextInput
          label="Service principal client ID (optional)"
          description="Leave blank to authenticate with a personal access token"
          value={clientId}
          onChange={(e) => {
            setClientId(e.currentTarget.value);
          }}
        />
        <PasswordInput
          label="Client secret or personal access token"
          required={true}
          value={secret}
          onChange={(e) => {
            setSecret(e.currentTarget.value);
          }}
        />
      </Stack>
    </Modal>
  );
}
